import React, { useEffect, useState } from "react";
import axiosInstance from "../../../interceptors";

// Side panel triggered by clicking a cell in QuestionScoresTable.
// Fetches /api/candidate-comparison/answers?userId=&questionId= and
// renders the candidate's per-attempt answer rows.
//
// No transcript text is stored — the closest substitute is the per-question
// ai_comment from candidate_video_score_t (joined server-side). The S3 link
// is the original audio/video; rendered as a download link.
export default function AnswerSidePanel({ open, candidate, question, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !candidate?.userId || !question?.questionId) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { userId: candidate.userId, questionId: question.questionId };
        const r = await axiosInstance.get("/job-posting/api/candidate-comparison/answers", { params });
        if (!cancelled) setData(r?.data || null);
      } catch (e) {
        if (!cancelled) setError(e?.message || e?.data?.error || "Failed to load answer");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, candidate?.userId, question?.questionId]);

  if (!open) return null;

  const answers = data?.answers || [];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-[500px] bg-white border-l border-slate-200 overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">Answer</div>
            <div className="text-sm font-semibold text-vf-base truncate" title={candidate?.fullName}>
              {candidate?.fullName}
            </div>
            <div className="text-xs text-vf-muted mt-0.5 line-clamp-2" title={question?.questionText}>
              {question?.questionText || `Question #${question?.questionId}`}
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-3 px-2 py-1 rounded text-xs text-vf-muted hover:bg-slate-100"
          >
            ✕
          </button>
        </div>

        <div className="p-4 space-y-4">
          {loading && <div className="text-sm text-vf-muted">Loading…</div>}
          {error && (
            <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700">{error}</div>
          )}
          {!loading && !error && answers.length === 0 && (
            <div className="text-sm text-vf-muted">No answer recorded.</div>
          )}
          {!loading && !error && answers.map((a, i) => <AnswerCard key={i} a={a} index={i} count={answers.length} />)}
          {!loading && !error && answers.length > 0 && (
            <div className="text-[11px] text-vf-muted pt-2 border-t border-slate-100">
              Transcripts are not stored per question — AI comment is the textual summary; the link below is the original recording.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnswerCard({ a, index, count }) {
  const score = a.score == null ? null : Number(a.score);
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      {count > 1 && (
        <div className="text-[10px] uppercase text-vf-muted tracking-wide mb-2">Attempt {index + 1}</div>
      )}
      <div className="flex flex-wrap gap-3 text-xs mb-2">
        <Meta label="Type">{a.answerType || a.questionType || "—"}</Meta>
        <Meta label="Score">{score == null ? "—" : score.toFixed(1)}</Meta>
        {a.isCorrect != null && (
          <Meta label="Correct">{a.isCorrect ? "Yes" : "No"}</Meta>
        )}
        {a.competency && <Meta label="Competency">{a.competency}</Meta>}
        {a.createdAt && <Meta label="At">{new Date(a.createdAt).toLocaleString()}</Meta>}
      </div>

      {a.aiComment ? (
        <div className="text-sm text-vf-base whitespace-pre-line leading-relaxed mb-2">{a.aiComment}</div>
      ) : (
        <div className="text-xs text-slate-400 italic mb-2">No AI comment for this answer.</div>
      )}

      {a.s3Link && (
        <a
          href={a.s3Link}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-vf-primary hover:underline"
        >
          Open recording ↗
        </a>
      )}
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <span>
      <span className="text-vf-muted">{label}:</span>{" "}
      <b className="text-vf-base">{children}</b>
    </span>
  );
}
